/**
 * Date format helper used by all 4 export format generators
 * (CSV, XML, JSON, Fixed-width). Closes the gap from R-12/R-14 where
 * `dateFormat` was declared on FieldContent and on each format-config
 * interface but never actually applied at render time.
 *
 * Per-field `dateFormat` (FieldContent.dateFormat) overrides format-level
 * `dateFormat` (CsvFormatConfig.dateFormat etc.); both fall back to
 * `'YYYY-MM-DD'` (ISO) if neither is set.
 *
 * Browser-safe: no `node:*` imports. Lives in `@capture/shared` so the
 * worker (server) and web (browser) consume the same logic.
 *
 * Supported tokens: YYYY, YY, MM, M, DD, D. Time tokens (HH/mm/ss) are
 * deferred to a future iteration — date-only is sufficient for v1.
 *
 * Pass-through policy: if the input is not a recognizable ISO date
 * (`YYYY-MM-DD` or full ISO datetime), the original value is returned
 * unchanged. This protects operator-typed strings (`05/09/2026`,
 * `May 9, 2026`) and unrecognized values from corruption.
 */
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T|$)/;
/**
 * Validates the shape of an ISO date string (year/month/day in correct ranges)
 * but intentionally does NOT calendar-validate month+day combinations — e.g.
 * `2026-02-31` parses successfully and renders as Feb 31 in the chosen format.
 * Calendar correctness is the upstream caller's responsibility (input
 * validation / OCR confidence gating); this helper's job is to format what
 * it's given without corrupting recognized inputs. Truly garbage strings
 * are caught by the pass-through policy in `formatDateValue`.
 */
function parseIsoDate(value) {
    const match = ISO_DATE_RE.exec(value);
    if (!match)
        return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (!Number.isFinite(year) ||
        !Number.isFinite(month) ||
        !Number.isFinite(day) ||
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > 31) {
        return null;
    }
    return { year, month, day };
}
function pad2(n) {
    return n < 10 ? `0${n}` : String(n);
}
/**
 * Tokens are ordered longest-first so a single left-to-right pass through
 * the format string never matches a shorter token inside a longer one
 * (e.g. `MM` must beat `M`, `DD` must beat `D`, `YYYY` must beat `YY`).
 */
const TOKEN_REGEX = /YYYY|YY|MM|M|DD|D/g;
function applyTokens(format, parsed) {
    return format.replace(TOKEN_REGEX, (token) => {
        switch (token) {
            case "YYYY":
                return String(parsed.year);
            case "YY":
                return String(parsed.year).slice(-2);
            case "MM":
                return pad2(parsed.month);
            case "M":
                return String(parsed.month);
            case "DD":
                return pad2(parsed.day);
            case "D":
                return String(parsed.day);
            default:
                return token;
        }
    });
}
export function formatDateValue(value, format) {
    if (value === null || value === undefined || value === "") {
        return "";
    }
    const str = typeof value === "string" ? value : String(value);
    const parsed = parseIsoDate(str);
    if (!parsed) {
        // Unrecognized input — preserve operator-typed values rather than corrupt them.
        return str;
    }
    // Default to ISO when no format is provided.
    const fmt = format && format.length > 0 ? format : "YYYY-MM-DD";
    return applyTokens(fmt, parsed);
}
//# sourceMappingURL=date-formatting.js.map